import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { plural } from '../../lib/plural';
import { LANG_LABEL } from '../../lib/siddhiText';
import { ArrowLeft } from 'lucide-react';
import '../../styles/AdminPage.css';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (iso) => (iso ? new Date(String(iso).length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const CHOICE = { resonates: 'resonates', saveForLater: 'saved for later', notForMe: 'not for me' };

const Stat = ({ label, value, testId }) => (
  <div className="admin-stat-card"><p className="admin-stat-label">{label}</p><p className="admin-stat-value" data-testid={testId}>{value}</p></div>
);

function BookBlock({ b }) {
  return (
    <section className="admin-table-section admin-user-detail" style={{ padding: 20, marginTop: 16 }} data-testid={`aud-book-${b.book_id}`}>
      <h3 style={{ margin: 0 }}>{b.book_title} <span className="admin-tab-count">{b.stage || '—'}</span></h3>
      {b.legacy && <p data-testid={`aud-legacy-${b.book_id}`}>Old system · Legacy install: {b.legacy.title} — {b.legacy.days_kept} of {b.legacy.target} days earlier{b.legacy.earlier_days > 0 ? ' · earlier practice (before 30-day window)' : ''}</p>}
      {b.seal && <p data-testid={`aud-seal-${b.book_id}`}>Sealed: {(b.seal.idea_titles || b.seal.idea_ids).join(' · ')} · {fmt(b.seal.sealed_at)}</p>}
      {b.bodha.length > 0 && <><h4>Bodha decisions</h4><ul data-testid={`aud-bodha-${b.book_id}`}>{b.bodha.map((c) => <li key={c.idea_id}>{c.title} — {CHOICE[c.choice] || c.choice} · {fmt(c.updated_at)}</li>)}</ul></>}
      {b.practices.length > 0 && (
        <>
          <h4>Sadhana — {b.checkins.length} day(s) kept</h4>
          <ul>
            {b.practices.map((p) => (
              <li key={p.idea_id}><strong>{p.title}</strong> — {p.checkins.length} day(s) · Phala “{p.phala || '—'}” · Vighna “{p.vighna || '—'}” · Upaya “{p.upaya || '—'}”</li>
            ))}
          </ul>
        </>
      )}
      {b.siddhi_versions.length > 0 && (
        <>
          <h4>Siddhi versions ({b.siddhi_versions.length})</h4>
          {b.siddhi_versions.map((v) => (
            <div key={v.version} data-testid={`aud-siddhi-${b.book_id}-v${v.version}`} style={{ marginBottom: 10 }}>
              <strong data-testid={`aud-siddhi-label-${b.book_id}-v${v.version}`}>Version {v.version}{v.legacy ? ' (legacy)' : ''} · {LANG_LABEL[v.lang] || LANG_LABEL.en}</strong> · {fmt(v.generated_at)}{v.note ? <> · note: <em>{v.note}</em></> : null}
              <ul>
                <li><em>Wish:</em> {v.woop?.wish}</li>
                <li><em>Outcome:</em> {v.woop?.outcome}</li>
                <li><em>Obstacle:</em> {v.woop?.obstacle}</li>
                <li><em>Plan:</em> {v.woop?.plan}</li>
                {v.woop?.arrived ? <li><em>What arrived:</em> {v.woop.arrived}</li> : null}
                {v.woop?.in_the_way ? <li><em>Vighna:</em> {v.woop.in_the_way}</li> : null}
                <li><em>Identity:</em> {v.identity_statement}</li>
                {v.checkins_at_generation ? <li><em>At generation:</em> {plural(v.checkins_at_generation.days_kept, 'day')} kept, longest run {v.checkins_at_generation.longest_run}, {plural(v.checkins_at_generation.misses, 'miss')} ({v.checkins_at_generation.recoveries} recovered)</li> : null}
              </ul>
            </div>
          ))}
        </>
      )}
      {b.chapters.length > 0 && <><h4>Chapters</h4><ul>{b.chapters.map((c) => <li key={c.id}>{fmt(c.timestamp)} — {c.reflection}</li>)}</ul></>}
      {b.whispers.length > 0 && <><h4>Whispers</h4><ul>{b.whispers.map((w) => <li key={w.id}>{fmt(w.timestamp)} — {w.reflection}</li>)}</ul></>}
    </section>
  );
}

export default function AdminUserDetailPage() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('icraftz_admin_token') || '';
    axios.get(`${API}/api/admin/users/${userId}/journey`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.status === 401 || e.response?.status === 403 ? 'Admin sign-in required — open /admin and sign in first.' : 'Failed to load user.'));
  }, [userId]);

  if (error) return <div className="admin-page"><p className="admin-error" data-testid="aud-error">{error}</p><Link to="/admin">Back to admin</Link></div>;
  if (!data) return <div className="admin-page"><p>Loading…</p></div>;
  const { user, overview, books } = data;
  const st = overview.stats;
  return (
    <div className="admin-page admin-dashboard" data-testid="admin-user-detail-page" style={{ padding: "72px 32px 48px", maxWidth: 1200, margin: "0 auto" }}>
      <header className="admin-header">
        <div>
          <Link to="/admin" data-testid="aud-back"><ArrowLeft size={14} /> Users</Link>{' · '}<Link to={`/admin/users/${user.id}/view`} data-testid="aud-view-dashboard">View dashboard</Link>
          <h1 className="admin-title" data-testid="aud-name">{user.name || user.email}</h1>
          <p className="admin-subtitle">{user.email} · signed up {fmt(user.created_at)} · {user.email_verified ? 'verified' : 'not verified'} · last active {fmt(overview.last_active)} · read-only</p>
        </div>
      </header>
      <section className="admin-stats" data-testid="aud-graphprintz">
        <Stat label="Consistency (30d)" value={st.available_days ? `${st.consistency_percent}%` : '—'} testId="aud-consistency" />
        <Stat label="Streak (cur / best)" value={`${st.current_streak} / ${st.best_streak}`} testId="aud-streak" />
        <Stat label="Days kept" value={st.total_kept_days} testId="aud-days-kept" />
        <Stat label="Practices" value={overview.practices.length} testId="aud-practices" />
        <Stat label="Furthest stage" value={overview.furthest_stage || '—'} testId="aud-stage" />
        <Stat label="Siddhis" value={overview.siddhis.length} testId="aud-siddhis" />
      </section>
      {books.map((b) => <BookBlock key={b.book_id} b={b} />)}
    </div>
  );
}
